import i18next from "i18next";
import { t } from "i18next";
import pluralize from "pluralize";
import { Platform } from "react-native";

const CARDS_TO_SHOW_SMALL = 2.0;
const CARDS_TO_SHOW_MEDIUM = 3.2;
const CARDS_TO_SHOW_LARGE = 3.5;

export const setOpacity = (color: string, opacity: number) => {
	if (color.startsWith("#")) {
		const bigint = Number.parseInt(color.replace("#", ""), 16);
		const r = (bigint >> 16) & 255;
		const g = (bigint >> 8) & 255;
		const b = bigint & 255;
		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	}

	const match = color.match(/^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/);
	if (!match) {
		throw new Error("Invalid color format");
	}
	const [_, r, g, b] = match;
	const alpha = Math.max(0, Math.min(100, opacity));
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function isEmptyList<T>(list: T[] | undefined) {
	return list === undefined || list.length === 0;
}
export function smartPluralize(
	singular: string,
	count: number,
	options?: { language?: string },
) {
	const language = options?.language || i18next.language;
	if (language === "en-US") {
		return pluralize(t(`common.${singular}`), count);
	}
	return t(`common.${singular}`, { count });
}

const formatCount = (text: string, count: number, reverse?: boolean) =>
	reverse
		? `${count} ${text}${count !== 1 ? "s" : ""}`
		: `${text}${count !== 1 ? "s" : ""}`;

const formatNumber = (num: number): string => num?.toLocaleString("en-US");

const convertToMinutes = (seconds: number) => Math.ceil(seconds / 60);

const formatSecondsToUTCtime = (seconds: number) => {
	const date = new Date(seconds * 1000);
	const hr = date.getUTCHours();
	const min = String(date.getUTCMinutes()).padStart(2, "0");
	const sec = String(date.getUTCSeconds()).padStart(2, "0");
	return hr ? `${hr}:${min}:${sec}` : `${min}:${sec}`;
};

const formatRank = (rank: number) => {
	if (!rank) {
		return null;
	}
	switch (rank) {
		case 1:
			return `${rank}st`;
		case 2:
			return `${rank}nd`;
		case 3:
			return `${rank}rd`;
		default:
			return `${rank}`;
	}
};
const titleCase = (s: string) => {
	const [c, ...rest] = s.split("");
	return `${c.toUpperCase()}${rest.join("")}`;
};

const isUuid = (value?: string | number): value is string => {
	const uuidRegex =
		/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
	return typeof value === "string" && uuidRegex.test(value);
};

const getPlatform = () => {
	const platform = Platform.OS;

	switch (platform) {
		case "ios":
			return "IOS";
		case "android":
			return "ANDROID";
		default:
			return "WEB";
	}
};

const calculateCardsToShow = (width: number) => {
	if (width < 480) {
		return CARDS_TO_SHOW_SMALL;
	}
	if (width < 720) {
		return CARDS_TO_SHOW_MEDIUM;
	}
	return CARDS_TO_SHOW_LARGE;
};

const getCurrentDate = () => {
	return new Date().toLocaleDateString();
};

const SAFE_SCHEMES = ["https", "mailto", "tel"] as const;

export const isSafeUrl = (
	url?: string,
	{
		allowedSchemes = SAFE_SCHEMES as readonly string[],
	}: { allowedSchemes?: readonly string[] } = {},
): boolean => {
	if (!url) {
		return false;
	}
	const schemeMatch = url.match(/^([a-z][a-z0-9+\-.]*):./i);
	if (!schemeMatch) {
		return false;
	}
	const scheme = schemeMatch[1].toLowerCase();
	return allowedSchemes.some((s) => s.toLowerCase() === scheme);
};

export {
	isEmptyList,
	formatCount,
	formatNumber,
	convertToMinutes,
	formatSecondsToUTCtime,
	formatRank,
	titleCase,
	isUuid,
	getPlatform,
	calculateCardsToShow,
	getCurrentDate,
};
